import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ArrowLeft, Save, AlertTriangle, Turtle, Zap, Volume2, Play } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { ttsLanguageOf, TTS_VOICES, DEFAULT_VOICE } from "@/lib/lang";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useDashboard } from "@/contexts/DashboardContext";

const LEVELS = ["débutant", "A1", "A2", "B1", "B2", "C1", "C2"];
const GOALS = ["conversation", "voyage", "professionnel", "examen", "immigration", "culture"];
const INTENSITIES = [10, 25, 50, 100];
const CORRECTIONS = ["douce", "standard", "intensive"];

/* Sample text used by the voice preview — a single phrase in the target language
 * generated on the fly for each voice option. Length capped to keep TTS latency
 * low. */
const VOICE_PREVIEW_TEXT = "Bonjour, écoutons ma voix pour votre parcours d'apprentissage.";

export default function ProfileSettings() {
    const [profile, setProfile] = useState(null);
    const [languages, setLanguages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [initialLang, setInitialLang] = useState(null);
    const [previewingVoice, setPreviewingVoice] = useState(null);
    const { refresh: refreshDashboard } = useDashboard();
    const navigate = useNavigate();

    useEffect(() => {
        (async () => {
            try {
                const [{ data: prof }, { data: langs }] = await Promise.all([
                    api.get("/profile/me"),
                    api.get("/languages"),
                ]);
                setProfile(prof.profile || {});
                setInitialLang(prof.profile?.target_language);
                setLanguages(langs.languages || []);
            } catch (err) {
                toast.error(formatApiError(err));
            } finally {
                setLoading(false);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
        // Intentional mount-only fetch — profile refresh is triggered by
        // save(), not by re-render.
    }, []);

    // Derived — auto-clears when the user reverts the language.
    const showLangWarn =
        !!initialLang && !!profile?.target_language && profile.target_language !== initialLang;

    function update(patch) {
        setProfile((p) => ({ ...p, ...patch }));
    }

    async function save() {
        setSaving(true);
        try {
            const payload = {
                first_name: profile.first_name || "",
                native_language: profile.native_language || "français",
                target_language: profile.target_language || "",
                variant: profile.variant || null,
                level: profile.level || "A1",
                goal: profile.goal || "conversation",
                goal_detail: profile.goal_detail || "",
                daily_minutes: Number(profile.daily_minutes || 20),
                intensity_words: Number(profile.intensity_words || 25),
                interests: profile.interests || [],
                modality: profile.modality || "multimodal",
                correction: profile.correction || "standard",
                program_length_days: Number(profile.program_length_days || 30),
                slow_pace: !!profile.slow_pace,
                voice: profile.voice || DEFAULT_VOICE,
                leaderboard_opt_in: !!profile.leaderboard_opt_in,
                completed_steps: profile.completed_steps || [],
            };
            await api.post("/profile/save", payload);
            toast.success("Profil mis à jour.");
            setInitialLang(payload.target_language);
            // Refresh the dashboard cache so every other page (Lesson, Podcasts,
            // Conversation, Intensive, Parcours…) picks up the new voice
            // preference on their next render.
            refreshDashboard?.();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSaving(false);
        }
    }

    async function previewVoice(voiceId) {
        if (previewingVoice) return;
        setPreviewingVoice(voiceId);
        try {
            const { data } = await api.post("/poc/tts", {
                text: VOICE_PREVIEW_TEXT,
                language: ttsLanguageOf(profile.target_language || "français"),
                speed: "natural",
                voice: voiceId,
            });
            const audio = new Audio(`data:${data.mime_type};base64,${data.audio_base64}`);
            await new Promise((resolve) => {
                audio.addEventListener("ended", resolve);
                audio.addEventListener("error", resolve);
                audio.play();
            });
        } catch (err) {
            toast.error(`Impossible de lire l'échantillon : ${formatApiError(err)}`);
        } finally {
            setPreviewingVoice(null);
        }
    }

    if (loading || !profile) {
        return (
            <div className="min-h-[60vh] flex items-center justify-center">
                <Loader2 className="w-6 h-6 text-terracotta animate-spin" />
            </div>
        );
    }

    return (
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12" data-testid="profile-settings-page">
            <button
                onClick={() => navigate("/dashboard")}
                className="text-inkSoft hover:text-ink text-sm inline-flex items-center gap-1 mb-4"
                data-testid="settings-back"
            >
                <ArrowLeft className="w-4 h-4" /> Tableau de bord
            </button>

            <p className="text-xs tracking-[0.28em] uppercase text-inkSoft mb-3">Profil</p>
            <h1 className="font-serif text-3xl md:text-4xl tracking-tight">Ajustez votre parcours.</h1>

            <div className="mt-8 space-y-5">
                {/* First name */}
                <div>
                    <label className="text-[10px] uppercase tracking-[0.22em] text-inkSoft">Prénom</label>
                    <input
                        value={profile.first_name || ""}
                        onChange={(e) => update({ first_name: e.target.value })}
                        data-testid="settings-first-name"
                        className="mt-1 w-full rounded-xl border border-lexiBorder bg-paper p-3 focus:outline-none focus:ring-2 focus:ring-terracotta text-sm"
                    />
                </div>

                {/* Target language */}
                <div>
                    <label className="text-[10px] uppercase tracking-[0.22em] text-inkSoft">Langue cible</label>
                    <Select
                        value={profile.target_language || ""}
                        onValueChange={(v) => update({ target_language: v })}
                    >
                        <SelectTrigger
                            data-testid="settings-target-language"
                            className="mt-1 w-full rounded-xl border border-lexiBorder bg-paper p-3 text-sm h-auto"
                        >
                            <SelectValue placeholder="Sélectionner une langue" />
                        </SelectTrigger>
                        <SelectContent className="max-h-72">
                            {languages.map((l) => (
                                <SelectItem key={l.iso} value={l.iso}>
                                    {l.fr}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    {showLangWarn && (
                        <div className="mt-2 flex items-start gap-2 rounded-xl border border-terracotta/40 bg-terracotta/8 px-3 py-2 text-xs" data-testid="settings-lang-warning">
                            <AlertTriangle className="w-3.5 h-3.5 text-terracotta shrink-0 mt-0.5" />
                            <p>
                                Attention : votre progression (mots appris, série, statistiques) est propre à votre langue cible actuelle.
                                Changer de langue démarrera un nouveau suivi — l&apos;ancien reste sauvegardé mais ne sera plus mis en avant.
                            </p>
                        </div>
                    )}
                </div>

                {/* Level */}
                <div>
                    <label className="text-[10px] uppercase tracking-[0.22em] text-inkSoft">Niveau CECRL</label>
                    <div className="mt-1 flex flex-wrap gap-2">
                        {LEVELS.map((lv) => (
                            <button
                                key={lv}
                                onClick={() => update({ level: lv })}
                                data-testid={`settings-level-${lv}`}
                                className={`px-3 py-1.5 rounded-full text-xs border ${profile.level === lv ? "bg-ink text-white border-ink" : "bg-paperAlt border-lexiBorder hover:bg-paper"}`}
                            >
                                {lv}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Goal */}
                <div>
                    <label className="text-[10px] uppercase tracking-[0.22em] text-inkSoft">Objectif principal</label>
                    <div className="mt-1 flex flex-wrap gap-2">
                        {GOALS.map((g) => (
                            <button
                                key={g}
                                onClick={() => update({ goal: g })}
                                data-testid={`settings-goal-${g}`}
                                className={`px-3 py-1.5 rounded-full text-xs border ${profile.goal === g ? "bg-emerald text-white border-emerald" : "bg-paperAlt border-lexiBorder hover:bg-paper"}`}
                            >
                                {g}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Daily minutes */}
                <div>
                    <label className="text-[10px] uppercase tracking-[0.22em] text-inkSoft">Temps quotidien (minutes)</label>
                    <input
                        type="number"
                        min={5}
                        max={180}
                        value={profile.daily_minutes || 20}
                        onChange={(e) => update({ daily_minutes: Number(e.target.value) })}
                        data-testid="settings-daily-minutes"
                        className="mt-1 w-32 rounded-xl border border-lexiBorder bg-paper p-2 focus:outline-none focus:ring-2 focus:ring-terracotta text-sm"
                    />
                </div>

                {/* Intensity */}
                <div>
                    <label className="text-[10px] uppercase tracking-[0.22em] text-inkSoft">
                        <Zap className="w-3 h-3 inline mr-1" /> Intensité (mots par jour)
                    </label>
                    <div className="mt-1 flex flex-wrap gap-2">
                        {INTENSITIES.map((n) => (
                            <button
                                key={n}
                                onClick={() => update({ intensity_words: n })}
                                data-testid={`settings-intensity-${n}`}
                                className={`px-3 py-1.5 rounded-full text-xs border ${profile.intensity_words === n ? "bg-terracotta text-white border-terracotta" : "bg-paperAlt border-lexiBorder hover:bg-paper"}`}
                            >
                                {n} mots
                                {n >= 50 && <span className="ml-1 text-[9px]">intensif</span>}
                            </button>
                        ))}
                    </div>
                    <p className="mt-1 text-[11px] text-inkSoft italic">
                        À 50 ou 100 mots, la « Leçon du jour » bascule sur le mode intensif par blocs.
                    </p>
                </div>

                {/* Correction */}
                <div>
                    <label className="text-[10px] uppercase tracking-[0.22em] text-inkSoft">Mode de correction</label>
                    <div className="mt-1 flex flex-wrap gap-2">
                        {CORRECTIONS.map((c) => (
                            <button
                                key={c}
                                onClick={() => update({ correction: c })}
                                data-testid={`settings-correction-${c}`}
                                className={`px-3 py-1.5 rounded-full text-xs border ${profile.correction === c ? "bg-ink text-white border-ink" : "bg-paperAlt border-lexiBorder hover:bg-paper"}`}
                            >
                                {c}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Slow pace */}
                <label className="flex items-start gap-3 cursor-pointer" data-testid="settings-slow-pace">
                    <input
                        type="checkbox"
                        checked={!!profile.slow_pace}
                        onChange={(e) => update({ slow_pace: e.target.checked })}
                        className="mt-1 accent-terracotta"
                    />
                    <div>
                        <div className="text-sm font-medium inline-flex items-center gap-1.5">
                            <Turtle className="w-3.5 h-3.5" /> Rythme ralenti
                        </div>
                        <div className="text-[11px] text-inkSoft">
                            Phrases plus courtes, TTS plus lent par défaut, consignes simplifiées. Vous pouvez aussi
                            activer cette option depuis la palette avec <code className="font-mono text-[10px]">/slow</code>.
                        </div>
                    </div>
                </label>

                {/* Weekly leaderboard opt-in (Phase 9) */}
                <label className="flex items-start gap-3 cursor-pointer" data-testid="settings-leaderboard-opt-in">
                    <input
                        type="checkbox"
                        checked={!!profile.leaderboard_opt_in}
                        onChange={(e) => update({ leaderboard_opt_in: e.target.checked })}
                        className="mt-1 accent-terracotta"
                        data-testid="settings-leaderboard-opt-in-checkbox"
                    />
                    <div>
                        <div className="text-sm font-medium">Classement hebdomadaire (optionnel)</div>
                        <div className="text-[11px] text-inkSoft">
                            Rejoindre le classement des 7 derniers jours (visible dans Progression → Classement).
                            Vous êtes toujours affiché·e sous pseudonyme (prénom + initiale), aucune donnée sensible n&apos;est publiée.
                        </div>
                    </div>
                </label>

                {/* Replay Lexia welcome intro (Phase 10) */}
                <div className="rounded-2xl border border-lexiBorder bg-paperAlt p-4 flex items-center justify-between gap-3" data-testid="settings-lexia-intro">
                    <div className="min-w-0">
                        <p className="text-sm font-medium">Présentation de Lexia</p>
                        <p className="text-[11px] text-inkSoft">
                            Revoir la séquence d&apos;accueil « Bonjour, moi c&apos;est Lexia ».
                        </p>
                    </div>
                    <button
                        onClick={async () => {
                            try {
                                await api.post("/profile/lexia-intro/replay");
                                toast.success("Rendez-vous sur le tableau de bord pour revoir la présentation.");
                                navigate("/dashboard");
                            } catch (err) {
                                toast.error(formatApiError(err));
                            }
                        }}
                        data-testid="settings-lexia-intro-replay"
                        className="inline-flex items-center gap-2 bg-white border border-lexiBorder hover:bg-paper text-ink rounded-full px-4 py-2 text-sm shrink-0 transition-colors"
                    >
                        Revoir
                    </button>
                </div>

                {/* Subscription — Phase 12 */}
                <div className="rounded-2xl border border-lexiBorder bg-paperAlt p-4 flex items-center justify-between gap-3" data-testid="settings-subscription-entry">
                    <div className="min-w-0">
                        <p className="text-sm font-medium">Abonnement</p>
                        <p className="text-[11px] text-inkSoft">
                            Gérer votre plan (mensuel / annuel), voir la date de renouvellement.
                        </p>
                    </div>
                    <button
                        onClick={() => navigate("/abonnement")}
                        data-testid="settings-subscription-open"
                        className="inline-flex items-center gap-2 bg-white border border-lexiBorder hover:bg-paper text-ink rounded-full px-4 py-2 text-sm shrink-0 transition-colors"
                    >
                        Voir les plans
                    </button>
                </div>

                {/* Voice preference */}
                <div className="rounded-2xl border border-lexiBorder bg-paper p-5" data-testid="voice-preferences">
                    <div className="flex items-center gap-2 mb-1">
                        <Volume2 className="w-4 h-4 text-terracotta" strokeWidth={1.75} />
                        <p className="text-[11px] tracking-[0.22em] uppercase text-inkSoft">Voix audio</p>
                    </div>
                    <h3 className="font-serif text-lg">Choisissez le timbre de votre professeur.</h3>
                    <p className="text-[11px] text-inkSoft mt-1">
                        Utilisé pour l&apos;écoute, la conversation, les podcasts et l&apos;audio des parcours.
                        Le timbre ne change pas l&apos;accent — celui-ci reste défini par votre langue cible.
                    </p>
                    <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {TTS_VOICES.map((v) => {
                            const selected = (profile.voice || DEFAULT_VOICE) === v.id;
                            return (
                                <div
                                    key={v.id}
                                    className={`rounded-xl border p-3 flex items-center justify-between gap-3 transition-colors ${selected
                                        ? "bg-emerald/10 border-emerald"
                                        : "bg-white border-lexiBorder hover:bg-paperAlt"}`}
                                    data-testid={`voice-${v.id}`}
                                >
                                    <button
                                        onClick={() => update({ voice: v.id })}
                                        className="flex-1 text-left"
                                        data-testid={`voice-${v.id}-select`}
                                    >
                                        <p className="text-sm font-medium">
                                            {v.label}
                                            {selected && <span className="ml-2 text-[10px] uppercase tracking-[0.2em] text-emerald">sélectionnée</span>}
                                        </p>
                                        <p className="text-[11px] text-inkSoft">{v.subtitle}</p>
                                    </button>
                                    <button
                                        onClick={() => previewVoice(v.id)}
                                        disabled={previewingVoice !== null}
                                        data-testid={`voice-${v.id}-preview`}
                                        className="inline-flex items-center gap-1 text-xs bg-ink text-white hover:bg-emerald rounded-full px-3 py-1.5 disabled:opacity-60 transition-colors"
                                    >
                                        {previewingVoice === v.id ? (
                                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                        ) : (
                                            <Play className="w-3.5 h-3.5" />
                                        )}
                                        Écouter
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                </div>

                <div className="flex justify-end pt-3">
                    <button
                        onClick={save}
                        disabled={saving}
                        data-testid="settings-save"
                        className="inline-flex items-center gap-2 bg-terracotta hover:bg-terracottaDark text-white rounded-full px-5 py-2.5 text-sm font-medium disabled:opacity-60"
                    >
                        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                        Enregistrer
                    </button>
                </div>
            </div>
        </div>
    );
}
